import { useTranslation } from 'react-i18next';
import {
    VStack,
    Text,
    Select,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    Checkbox,
    Divider,
    Box,
} from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { useAppStore } from '../../state/store';

export function ViewCubeOptionsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.viewCubeOptionsOpen);
    const onClose = useAppStore((state) => state.toggleViewCubeOptions);
    const options = useAppStore((state) => state.viewCubeOptions);
    const updateOptions = useAppStore((state) => state.updateViewCubeOptions);

    const handleRestoreDefaults = () => {
        updateOptions({
            showViewCube: true,
            showCompass: true,
            snapToView: true,
            preserveSceneUpright: true,
            inverseCompassRotation: true,
            opacity: 100,
            size: 100,
            position: 'Bottom Right',
        });
    };

    const footer = (
        <>
            <DialogButton variant="ghost" onClick={handleRestoreDefaults}>{t('misc.restoreDefaults')}</DialogButton>
            <DialogButton variant="primary" onClick={onClose}>{t('misc.oK')}</DialogButton>
        </>); return (<AppDialog isOpen={isOpen} onClose={onClose} size="md" title={t('misc.viewCubeOptions')} footer={footer}>
                    <VStack spacing={4} align="stretch">
                        <Checkbox
                            isChecked={options.showViewCube}
                            onChange={(e) => updateOptions({ showViewCube: e.target.checked })}
                            sx={{
                                'span.chakra-checkbox__control': {
                                    borderColor: 'var(--color-border)',
                                    bg: 'var(--color-bg-tertiary)',
                                    _checked: {
                                        bg: 'var(--color-accent)',
                                        borderColor: 'var(--color-accent)',
                                    }
                                },
                                'span.chakra-checkbox__label': {
                                    fontSize: 'var(--font-size-sm)'
                                }
                            }}
                        >{t('misc.showTheViewCube')}</Checkbox>

                        <Divider borderColor="var(--color-border)" />

                        <Box>
                            <Text mb={2} fontWeight="bold" fontSize="var(--font-size-sm)">{t('misc.onScreenPosition')}</Text>
                            <Select
                                value={options.position}
                                onChange={(e) => updateOptions({ position: e.target.value as any })}
                                size="sm"
                                bg="var(--color-bg-tertiary)"
                                borderColor="var(--color-border)"
                                color="var(--color-text-primary)"
                                sx={{
                                    option: { bg: 'var(--color-bg-tertiary)' }
                                }}
                            >
                                <option value="Top Right">{t('misc.topRight')}</option>
                                <option value="Top Left">{t('misc.topLeft')}</option>
                                <option value="Bottom Right">{t('misc.bottomRight')}</option>
                                <option value="Bottom Left">{t('misc.bottomLeft')}</option>
                            </Select>
                        </Box>

                        <Box>
                            <Text mb={2} fontWeight="bold" fontSize="var(--font-size-sm)">{t('misc.viewCubeSize')}: {options.size}%</Text>
                            <Slider
                                value={options.size}
                                onChange={(val) => updateOptions({ size: val })}
                                min={50}
                                max={150}
                                step={5}
                            >
                                <SliderTrack bg="var(--color-bg-tertiary)">
                                    <SliderFilledTrack bg="var(--color-accent)" />
                                </SliderTrack>
                                <SliderThumb boxSize={4} bg="var(--color-text-primary)" />
                            </Slider>
                        </Box>

                        <Box>
                            <Text mb={2} fontWeight="bold" fontSize="var(--font-size-sm)">Inactive opacity: {options.opacity}%</Text>
                            <Slider
                                value={options.opacity}
                                onChange={(val) => updateOptions({ opacity: val })}
                                min={0}
                                max={100}
                                step={1}
                            >
                                <SliderTrack bg="var(--color-bg-tertiary)">
                                    <SliderFilledTrack bg="var(--color-accent)" />
                                </SliderTrack>
                                <SliderThumb boxSize={4} bg="var(--color-text-primary)" />
                            </Slider>
                        </Box>

                        <Divider borderColor="var(--color-border)" />

                        <Checkbox
                            isChecked={options.showCompass}
                            onChange={(e) => updateOptions({ showCompass: e.target.checked })}
                            sx={{
                                'span.chakra-checkbox__control': {
                                    borderColor: 'var(--color-border)',
                                    bg: 'var(--color-bg-tertiary)',
                                    _checked: {
                                        bg: 'var(--color-accent)',
                                        borderColor: 'var(--color-accent)',
                                    }
                                },
                                'span.chakra-checkbox__label': {
                                    fontSize: 'var(--font-size-sm)'
                                }
                            }}
                        >{t('misc.showTheCompass')}</Checkbox>

                        <Checkbox
                            isChecked={options.snapToView}
                            onChange={(e) => updateOptions({ snapToView: e.target.checked })}
                            sx={{
                                'span.chakra-checkbox__control': {
                                    borderColor: 'var(--color-border)',
                                    bg: 'var(--color-bg-tertiary)',
                                    _checked: {
                                        bg: 'var(--color-accent)',
                                        borderColor: 'var(--color-accent)',
                                    }
                                },
                                'span.chakra-checkbox__label': {
                                    fontSize: 'var(--font-size-sm)'
                                }
                            }}
                        >{t('misc.snapToClosestView')}</Checkbox>

                        <Checkbox
                            isChecked={options.preserveSceneUpright}
                            onChange={(e) => updateOptions({ preserveSceneUpright: e.target.checked })}
                            sx={{
                                'span.chakra-checkbox__control': {
                                    borderColor: 'var(--color-border)',
                                    bg: 'var(--color-bg-tertiary)',
                                    _checked: {
                                        bg: 'var(--color-accent)',
                                        borderColor: 'var(--color-accent)',
                                    }
                                },
                                'span.chakra-checkbox__label': {
                                    fontSize: 'var(--font-size-sm)'
                                }
                            }}
                        >{t('misc.preserveSceneUpright')}</Checkbox>

                        <Checkbox
                            isChecked={options.inverseCompassRotation}
                            onChange={(e) => updateOptions({ inverseCompassRotation: e.target.checked })}
                            sx={{
                                'span.chakra-checkbox__control': {
                                    borderColor: 'var(--color-border)',
                                    bg: 'var(--color-bg-tertiary)',
                                    _checked: {
                                        bg: 'var(--color-accent)',
                                        borderColor: 'var(--color-accent)',
                                    }
                                },
                                'span.chakra-checkbox__label': {
                                    fontSize: 'var(--font-size-sm)'
                                }
                            }}
                        >{t('misc.invertCompassRotation')}</Checkbox>
                    </VStack>
        </AppDialog>
    );
}
